import { useCallback, useEffect, useRef, useState } from "react";
import { z } from "zod";

import type { DashboardRealtimeMessage } from "../../realtime/dashboardRealtimeTypes";
import type { ActorAttribution, BlackboardInstance } from "./blackboardModels";
import type { SpawnInput } from "./SpawnDialog";

import { useDashboardRealtime } from "../../hooks/useDashboardRealtime";
import {
  applyInstanceActivityLifecycleDelta,
  applyInstanceStatusChangedDelta,
  applyLocalKillToInstances,
  applyRealtimeInstanceUpdate,
  buildOptimisticSpawnInstance,
  isInstanceStatus,
  mergeFetchedInstancesWithPrior,
  readMessageMarker,
  readNullableString,
  readStringArray,
  shouldApplyInstanceRealtimeMarker,
  acceptInstanceActivityRealtimeMarker,
  shouldRefetchRunAggregate,
  shouldRefreshBlackboardForRealtimeMessage,
} from "./instanceRealtime";
import { readLifecycleError } from "./useInstanceLifecycleActions";

type RestBlackboardInstance = Omit<
  BlackboardInstance,
  "contextWindow" | "lastCacheStats"
> & { contextGrowthSinceEvolution: number; contextWindowSize: number };

const instancesResponseSchema = z.custom<RestBlackboardInstance[]>();
// The dashboard spawn route returns { instanceId, sharedChannelId, status }
// (dashboard/api/user/instances.ts) — a single optional shared channel, NOT a
// channelIds array. Requiring more here turns a successful spawn into a false
// client-side failure.
export const spawnResponseSchema = z
  .object({
    instanceId: z.string(),
    sharedChannelId: z.string().nullable().optional(),
  })
  .passthrough();
const sendMessageResponseSchema = z
  .object({ message_id: z.string() })
  .passthrough();

/** A spawn rejected by the backend, carrying the actionable message the
 *  orchestrator produced (e.g. "this channel already has a VULCAN" for a 409
 *  duplicate-template conflict) rather than a status-only string. The spawn
 *  modal renders `message` inline; the page-level banner is never populated
 *  for spawn failures because the Modal overlay occludes it. */
export interface SpawnFailure {
  kind: "spawn_failure";
  /** Backend error code (`duplicate_name`, `singleton_contended`, …), null
   *  when the body was malformed and only the status was usable. */
  error: string | null;
  /** Operator-facing message. Falls back to generic copy for malformed bodies. */
  message: string;
  status: number;
}

export const GENERIC_SPAWN_FAILURE_MESSAGE =
  "Spawn failed — the agent could not be created. Check the template and try again.";

export function isSpawnFailure(value: unknown): value is SpawnFailure {
  return (
    typeof value === "object" &&
    value !== null &&
    (value as { kind?: unknown }).kind === "spawn_failure"
  );
}

/**
 * Build a typed spawn failure from a non-OK spawn response. The dashboard
 * proxy forwards the orchestrator's `{error, message}` body verbatim, so the
 * actionable text is already there — it was previously discarded in favour of
 * `Failed to spawn instance: <status>`. Malformed/empty bodies fall back to
 * the generic copy so the modal never renders a blank reason.
 */
export async function readSpawnFailure(
  response: Response,
): Promise<SpawnFailure> {
  const parsed: unknown = await response.json().catch(() => null);
  const record =
    typeof parsed === "object" && parsed !== null
      ? (parsed as { error?: unknown; message?: unknown })
      : null;
  const message =
    typeof record?.message === "string" && record.message.trim().length > 0
      ? record.message
      : GENERIC_SPAWN_FAILURE_MESSAGE;
  return {
    error: typeof record?.error === "string" ? record.error : null,
    kind: "spawn_failure",
    message,
    status: response.status,
  };
}

export async function readKillInstanceErrorMessage(
  response: Response,
): Promise<string> {
  const fallback = `Failed to kill instance: ${response.status}`;
  const bodyText = await response.text().catch(() => "");
  const trimmedBody = bodyText.trim();
  if (trimmedBody.length === 0) {
    return fallback;
  }
  if (response.headers.get("content-type")?.includes("application/json")) {
    try {
      const parsed: unknown = JSON.parse(trimmedBody);
      return readLifecycleError(parsed) ?? fallback;
    } catch {
      return trimmedBody;
    }
  }
  return trimmedBody;
}

export interface SpawnSuccess {
  channelIds: string[];
  id: string;
}

export type SpawnResult = SpawnFailure | SpawnSuccess;

interface UseBlackboardResult {
  actorAttributions: ActorAttribution[];
  instances: BlackboardInstance[];
  /** Set of instance IDs currently generating tokens (live LLM call in progress). */
  generatingInstances: ReadonlySet<string>;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  spawnInstance: (input: SpawnInput) => Promise<SpawnResult>;
  killInstance: (instanceId: string) => Promise<void>;
  sendMessage: (
    instanceId: string,
    channelId: string,
    message: string,
  ) => Promise<string>;
}

interface UseBlackboardOptions {
  includeKilledAttributions?: boolean;
}

// How long after the last token_delta before we clear the "generating" state.
const GENERATING_TTL_MS = 3000;

/** Debounce for the runCount convergence refetch triggered by a new-run wake. */
export const RUN_AGGREGATE_REFRESH_DEBOUNCE_MS = 1500;

export function splitDashboardInstances(instances: BlackboardInstance[]) {
  return {
    actorAttributions: instances.map(({ avatarSeed, displayName, id }) => ({
      avatarSeed,
      displayName,
      id,
    })),
    instances: instances.filter((instance) => instance.status !== "killed"),
  };
}

export function useBlackboard(
  options: UseBlackboardOptions = {},
): UseBlackboardResult {
  const { includeKilledAttributions = false } = options;
  const [actorAttributions, setActorAttributions] = useState<
    ActorAttribution[]
  >([]);
  const [instances, setInstances] = useState<BlackboardInstance[]>([]);
  const [generatingInstances, setGeneratingInstances] = useState<
    ReadonlySet<string>
  >(new Set());
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const refreshTimeoutRef = useRef<number | null>(null);
  const runAggregateTimeoutRef = useRef<number | null>(null);
  const hasConnectedRef = useRef(false);
  const instanceRealtimeMarkersRef = useRef<Map<string, number>>(new Map());
  // Last run id known per instance, seeded from REST and advanced by wake
  // events. Used only to detect that a wake introduced a NEW durable run, so
  // the canonical `runCount` aggregate can be refetched (never incremented).
  const instanceRunIdsRef = useRef<Map<string, string | null>>(new Map());
  // Map of instanceId → timer handle for auto-clearing generating state.
  const generatingTimersRef = useRef<Map<string, number>>(new Map());
  const { status, subscribe } = useDashboardRealtime();

  const fetchInstances = useCallback(async () => {
    const snapshotMarker = Date.now();
    try {
      setLoading(true);
      setErrorMessage(null);
      const response = await fetch(
        includeKilledAttributions
          ? "/user/instances?includeKilled=true"
          : "/user/instances",
        { credentials: "include" },
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch instances: ${response.status}`);
      }
      const raw = instancesResponseSchema.parse(await response.json());
      const data: BlackboardInstance[] = raw.map((inst) => ({
        ...inst,
        // Seed context window from REST data; phaseBudgetRemaining and currentPhase
        // Are live WS fields — default to budget=200K and phase="act" until first WS event.
        contextWindow:
          inst.contextWindowSize > 0 || inst.contextGrowthSinceEvolution > 0
            ? {
                contextGrowthSinceEvolution: inst.contextGrowthSinceEvolution,
                contextWindowSize: inst.contextWindowSize,
                currentPhase: "act",
                phaseBudgetRemaining: 200_000,
              }
            : null,
        // Cache stats populated via WS cache_stats events after each activation.
        lastCacheStats: null,
        lastChannelLockWaitMs: null,
      }));
      const dashboardInstances = splitDashboardInstances(data);
      // Re-seed the run-id baseline from the authoritative snapshot: the
      // refetch that converged `runCount` also fixes what "current run" means,
      // so a subsequent wake on the same run doesn't refetch again.
      for (const instance of dashboardInstances.instances) {
        instanceRunIdsRef.current.set(instance.id, instance.currentRunId);
      }
      setActorAttributions(dashboardInstances.actorAttributions);
      setInstances((prev) =>
        mergeFetchedInstancesWithPrior(
          dashboardInstances.instances,
          prev,
          instanceRealtimeMarkersRef.current,
          snapshotMarker,
        ),
      );
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : "Failed to fetch instances",
      );
    } finally {
      setLoading(false);
    }
  }, [includeKilledAttributions]);

  const clearAllGeneratingInstances = useCallback(() => {
    for (const handle of generatingTimersRef.current.values()) {
      window.clearTimeout(handle);
    }
    generatingTimersRef.current.clear();
    setGeneratingInstances(new Set());
  }, []);

  // Drop the "generating" marker and its expiry timer for one instance. Lives
  // at hook scope, not inside the WS effect, because a local kill has to clear
  // the same state the server's killed event would have cleared — see
  // killInstance.
  const clearLiveActivationState = useCallback((instanceId: string) => {
    const timers = generatingTimersRef.current;
    const handle = timers.get(instanceId);
    if (handle !== undefined) {
      window.clearTimeout(handle);
      timers.delete(instanceId);
    }
    setGeneratingInstances((prev) => {
      if (!prev.has(instanceId)) {
        return prev;
      }
      const next = new Set(prev);
      next.delete(instanceId);
      return next;
    });
  }, []);

  // Initial fetch on mount.
  useEffect(() => {
    void fetchInstances();
  }, [fetchInstances]);

  // Heal stale fleet state after a WS reconnect. The realtime FSM greens the
  // status pill on reopen but carries no replay cursor, so a structural event
  // (spawn/kill/membership) missed during the outage would persist until the
  // next live event. Refetch on every reopen except the first (mount already
  // fetched). A ref gates this rather than the connecting/reconnecting label,
  // because a fast reconnect can report "connecting" — indistinguishable from
  // the initial connect. merge-preserve keeps live WS fields, so this refetch
  // does not flicker the HUD to defaults.
  useEffect(() => {
    if (status !== "open") {
      return;
    }
    if (hasConnectedRef.current) {
      clearAllGeneratingInstances();
      void fetchInstances();
    }
    hasConnectedRef.current = true;
  }, [status, fetchInstances, clearAllGeneratingInstances]);

  // WS subscription — apply in-place deltas for instance events.
  useEffect(() => {
    const scheduleRefresh = () => {
      if (refreshTimeoutRef.current !== null) {
        return;
      }
      refreshTimeoutRef.current = window.setTimeout(() => {
        refreshTimeoutRef.current = null;
        void fetchInstances();
      }, 250);
    };

    // Debounced convergence refetch for DB aggregates (runCount) that no WS
    // payload carries. Coalesces a burst of wakes across instances into ONE
    // instance-list fetch; longer than scheduleRefresh's 250ms because the run
    // row is written around the wake, not before it.
    const scheduleRunAggregateRefresh = () => {
      if (runAggregateTimeoutRef.current !== null) {
        return;
      }
      runAggregateTimeoutRef.current = window.setTimeout(() => {
        runAggregateTimeoutRef.current = null;
        void fetchInstances();
      }, RUN_AGGREGATE_REFRESH_DEBOUNCE_MS);
    };

    const markGenerating = (instanceId: string) => {
      const timers = generatingTimersRef.current;
      // Clear any existing expiry timer.
      const existing = timers.get(instanceId);
      if (existing !== undefined) {
        window.clearTimeout(existing);
      }
      // Add to generating set.
      setGeneratingInstances((prev) => {
        if (prev.has(instanceId)) {
          return prev;
        }
        const next = new Set(prev);
        next.add(instanceId);
        return next;
      });
      // Schedule auto-clear after TTL.
      const handle = window.setTimeout(() => {
        timers.delete(instanceId);
        setGeneratingInstances((prev) => {
          if (!prev.has(instanceId)) {
            return prev;
          }
          const next = new Set(prev);
          next.delete(instanceId);
          return next;
        });
      }, GENERATING_TTL_MS);
      timers.set(instanceId, handle);
    };

    function applyStatusChanged(
      message: Extract<
        DashboardRealtimeMessage,
        { type: "instance_status_changed" }
      >,
      marker: ReturnType<typeof readMessageMarker>,
    ) {
      const { payload } = message;
      const instanceId =
        typeof payload.instance_id === "string" ? payload.instance_id : null;
      const rawStatus =
        typeof payload.status === "string" ? payload.status : null;
      if (!instanceId || rawStatus === null) {
        scheduleRefresh();
        return;
      }
      const markers = instanceRealtimeMarkersRef.current;
      if (!shouldApplyInstanceRealtimeMarker(markers.get(instanceId), marker)) {
        return;
      }
      markers.set(instanceId, marker);
      if (rawStatus === "deleted") {
        setActorAttributions((previous) =>
          previous.filter((attribution) => attribution.id !== instanceId),
        );
        setInstances(
          (prev) =>
            applyInstanceStatusChangedDelta(prev, markers, payload, marker)
              .instances,
        );
        clearLiveActivationState(instanceId);
        return;
      }
      const newStatus = isInstanceStatus(rawStatus) ? rawStatus : null;
      if (!newStatus) {
        scheduleRefresh();
        return;
      }
      setInstances(
        (prev) =>
          applyInstanceStatusChangedDelta(prev, markers, payload, marker)
            .instances,
      );
      if (newStatus === "waiting" || newStatus === "killed") {
        clearLiveActivationState(instanceId);
      }
      return;
    }

    function applyActivityMetrics(
      message: Extract<DashboardRealtimeMessage, { type: "instance_activity" }>,
      instanceId: string | null,
      activityCurrentRunId: string | null | undefined,
    ) {
      const { payload } = message;
      if (instanceId && payload.activity_type === "token_delta") {
        markGenerating(instanceId);
        // Update instance.tokenUsage + totalCostUsd live from token_delta events.
        // Each event carries one completed LLM step's marginal usage, so fold
        // It into the instance summary immediately from the WS fanout.
        const { detail } = payload;
        if (detail) {
          const inputDelta =
            typeof detail.input_tokens_delta === "number"
              ? detail.input_tokens_delta
              : 0;
          const outputDelta =
            typeof detail.output_tokens_delta === "number"
              ? detail.output_tokens_delta
              : 0;
          const cacheReadDelta =
            typeof detail.cache_read_tokens_delta === "number"
              ? detail.cache_read_tokens_delta
              : 0;
          const cacheWriteDelta =
            typeof detail.cache_write_tokens_delta === "number"
              ? detail.cache_write_tokens_delta
              : 0;
          const costDelta =
            typeof detail.cost_delta_usd === "number"
              ? detail.cost_delta_usd
              : 0;
          const noCacheDelta =
            typeof detail.no_cache_tokens_delta === "number"
              ? detail.no_cache_tokens_delta
              : null;
          setInstances((prev) =>
            prev.map((inst) =>
              inst.id === instanceId
                ? {
                    ...inst,
                    ...(activityCurrentRunId !== undefined && {
                      currentRunId: activityCurrentRunId,
                    }),
                    lastCacheStats:
                      noCacheDelta !== null && cacheReadDelta + noCacheDelta > 0
                        ? {
                            cacheHitPct:
                              (cacheReadDelta /
                                (cacheReadDelta + noCacheDelta)) *
                              100,
                            isBreak: false,
                          }
                        : inst.lastCacheStats,
                    tokenUsage: {
                      cacheWriteTokens:
                        inst.tokenUsage.cacheWriteTokens + cacheWriteDelta,
                      cachedInputTokens:
                        inst.tokenUsage.cachedInputTokens + cacheReadDelta,
                      inputTokens: inst.tokenUsage.inputTokens + inputDelta,
                      outputTokens: inst.tokenUsage.outputTokens + outputDelta,
                    },
                    totalCostUsd: (inst.totalCostUsd ?? 0) + costDelta,
                  }
                : inst,
            ),
          );
        }
      }
      if (instanceId && payload.activity_type === "evolution_cycle") {
        const { detail } = payload;
        const newEvolution =
          typeof detail.evolution === "number" ? detail.evolution : null;
        if (newEvolution !== null) {
          setInstances((prev) =>
            prev.map((inst) =>
              inst.id === instanceId
                ? {
                    ...inst,
                    ...(activityCurrentRunId !== undefined && {
                      currentRunId: activityCurrentRunId,
                    }),
                    evolution: newEvolution,
                  }
                : inst,
            ),
          );
        }
      }
      if (instanceId && payload.activity_type === "context_window") {
        const { detail } = payload;
        const contextWindowSize =
          detail !== null && typeof detail.context_window_size === "number"
            ? detail.context_window_size
            : null;
        const contextGrowthSinceEvolution =
          detail !== null &&
          typeof detail.context_growth_since_evolution === "number"
            ? detail.context_growth_since_evolution
            : null;
        const phaseBudgetRemaining =
          detail !== null && typeof detail.phase_budget_remaining === "number"
            ? detail.phase_budget_remaining
            : null;
        const currentPhase =
          detail !== null && typeof detail.current_phase === "string"
            ? detail.current_phase
            : null;
        if (
          contextWindowSize !== null &&
          contextGrowthSinceEvolution !== null &&
          phaseBudgetRemaining !== null &&
          currentPhase !== null
        ) {
          setInstances((prev) =>
            prev.map((inst) =>
              inst.id === instanceId
                ? {
                    ...inst,
                    ...(activityCurrentRunId !== undefined && {
                      currentRunId: activityCurrentRunId,
                    }),
                    contextWindow: {
                      contextGrowthSinceEvolution,
                      contextWindowSize,
                      currentPhase,
                      phaseBudgetRemaining,
                    },
                  }
                : inst,
            ),
          );
        }
      }
      if (instanceId && payload.activity_type === "cache_stats") {
        const { detail } = payload;
        const cacheHitPct =
          detail !== null && typeof detail.cache_hit_pct === "number"
            ? detail.cache_hit_pct
            : null;
        const isBreak =
          detail !== null && typeof detail.is_break === "boolean"
            ? detail.is_break
            : false;
        setInstances((prev) =>
          prev.map((inst) =>
            inst.id === instanceId
              ? {
                  ...inst,
                  ...(activityCurrentRunId !== undefined && {
                    currentRunId: activityCurrentRunId,
                  }),
                  lastCacheStats: { cacheHitPct, isBreak },
                }
              : inst,
          ),
        );
      }
    }

    const unsubscribe = subscribe(
      (message) => {
        const { type, payload } = message;
        const marker = readMessageMarker(message);

        // Instance spawned and channel membership changes need a full refresh
        // because the dashboard event does not carry complete actor rows.
        if (shouldRefreshBlackboardForRealtimeMessage(message)) {
          scheduleRefresh();
          return;
        }

        // Instance status changed — apply in-place delta.
        if (type === "instance_status_changed") {
          applyStatusChanged(message, marker);
          return;
        }

        // Instance activity: track token_delta for live generating indicator,
        // And status_change for park/wake transitions that the node emits.
        if (type === "instance_activity") {
          const instanceId =
            typeof payload.instance_id === "string"
              ? payload.instance_id
              : null;
          const activityType =
            typeof payload.activity_type === "string"
              ? payload.activity_type
              : null;
          const activityCurrentRunId = readNullableString(
            payload.current_run_id,
          );
          if (
            instanceId &&
            !acceptInstanceActivityRealtimeMarker(
              instanceRealtimeMarkersRef.current,
              instanceId,
              activityType,
              marker,
            )
          ) {
            return;
          }
          if (payload.activity_type === "channel_message") {
            const { detail } = payload;
            const runningInstanceIds = new Set([
              ...readStringArray(detail?.running_instance_ids),
              ...readStringArray(detail?.woken_instance_ids),
            ]);
            if (runningInstanceIds.size > 0) {
              setInstances((prev) =>
                prev.map((inst) =>
                  runningInstanceIds.has(inst.id) &&
                  shouldApplyInstanceRealtimeMarker(
                    instanceRealtimeMarkersRef.current.get(inst.id),
                    marker,
                  )
                    ? // A fanout naming a killed actor as "running" is late
                      // telemetry, never a resurrection (R: killed is
                      // absorbing).
                      applyRealtimeInstanceUpdate(inst, null, (current) => ({
                        ...current,
                        status: "running",
                        wakeBlockedAt: null,
                        wakeBlockedConsecutiveFailureCount: null,
                        wakeBlockedError: null,
                        wakeBlockedReason: null,
                        wakeBlockedTriggerChannelMessageId: null,
                        wakeBlockedUntil: null,
                      }))
                    : inst,
                ),
              );
              for (const id of runningInstanceIds) {
                const lastMarker = instanceRealtimeMarkersRef.current.get(id);
                if (shouldApplyInstanceRealtimeMarker(lastMarker, marker)) {
                  instanceRealtimeMarkersRef.current.set(id, marker);
                }
              }
            }
          }
          applyActivityMetrics(message, instanceId, activityCurrentRunId);
          if (
            instanceId &&
            (activityType === "channel_lock_waiting" ||
              activityType === "channel_lock_acquired" ||
              activityType === "actor_wake" ||
              activityType === "consolidation_only_wake" ||
              activityType === "external_compact_seed" ||
              activityType === "actor_park")
          ) {
            setInstances(
              (prev) =>
                applyInstanceActivityLifecycleDelta(
                  prev,
                  payload,
                  message.timestamp,
                ).instances,
            );
            if (activityType === "actor_park") {
              clearLiveActivationState(instanceId);
            }
            // A wake that introduces a run id we haven't seen for this
            // instance means a new durable run row exists; `runCount` is a DB
            // aggregate the WS payload doesn't carry, so converge it with one
            // debounced refetch. Same-run wakes (healthy parked actor) and
            // unknown instances refetch nothing.
            if (activityType === "actor_wake") {
              const runIds = instanceRunIdsRef.current;
              if (
                shouldRefetchRunAggregate(
                  runIds.get(instanceId),
                  activityCurrentRunId,
                )
              ) {
                runIds.set(instanceId, activityCurrentRunId ?? null);
                scheduleRunAggregateRefresh();
              }
            }
          }
          if (instanceId && payload.activity_type === "status_change") {
            const { detail } = payload;
            const rawStatus =
              detail !== null && typeof detail.status === "string"
                ? detail.status
                : null;
            const newStatus =
              rawStatus !== null && isInstanceStatus(rawStatus)
                ? rawStatus
                : null;
            if (newStatus) {
              setInstances((prev) =>
                prev.map((inst) =>
                  inst.id === instanceId
                    ? applyRealtimeInstanceUpdate(
                        inst,
                        newStatus,
                        (current) => ({
                          ...current,
                          ...(activityCurrentRunId !== undefined && {
                            currentRunId: activityCurrentRunId,
                          }),
                          ...((newStatus === "waiting" ||
                            newStatus === "killed") && {
                            channelLockWaiting: false,
                            inFlightStepKind: null,
                            inFlightWakeState: null,
                          }),
                          status: newStatus,
                        }),
                      )
                    : inst,
                ),
              );
              if (newStatus === "waiting" || newStatus === "killed") {
                clearLiveActivationState(instanceId);
              }
            }
          }
        }
      },
      { topic: "internalEventTopic" },
    );

    const generatingTimers = generatingTimersRef.current;
    return () => {
      unsubscribe();
      if (refreshTimeoutRef.current !== null) {
        window.clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
      if (runAggregateTimeoutRef.current !== null) {
        window.clearTimeout(runAggregateTimeoutRef.current);
        runAggregateTimeoutRef.current = null;
      }
      // Clear all generating timers on unmount.
      for (const handle of generatingTimers.values()) {
        window.clearTimeout(handle);
      }
      generatingTimers.clear();
    };
  }, [subscribe, fetchInstances, clearLiveActivationState]);

  const spawnInstance = useCallback(
    async (input: SpawnInput): Promise<SpawnResult> => {
      try {
        const response = await fetch("/user/instances", {
          body: JSON.stringify(spawnRequestBody(input)),
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          method: "POST",
        });
        if (!response.ok) {
          // Return the backend's actionable reason WITHOUT touching
          // `errorMessage`: the spawn modal owns this failure, and the
          // page-level banner renders behind the Modal overlay where the
          // operator can't see it.
          return await readSpawnFailure(response);
        }
        const parsed = spawnResponseSchema.safeParse(await response.json());
        if (!parsed.success) {
          return {
            error: "malformed_spawn_response",
            kind: "spawn_failure",
            message: GENERIC_SPAWN_FAILURE_MESSAGE,
            status: response.status,
          };
        }
        const { instanceId, sharedChannelId } = parsed.data;
        const channelIds = sharedChannelId ? [sharedChannelId] : [];
        const optimisticInstance = buildOptimisticSpawnInstance(
          input,
          { channelIds, instanceId },
          new Date().toISOString(),
        );
        setInstances((prev) => [
          optimisticInstance,
          ...prev.filter((inst) => inst.id !== instanceId),
        ]);
        setActorAttributions((previous) => [
          {
            avatarSeed: optimisticInstance.avatarSeed,
            displayName: optimisticInstance.displayName,
            id: optimisticInstance.id,
          },
          ...previous.filter(
            (attribution) => attribution.id !== optimisticInstance.id,
          ),
        ]);
        return { channelIds, id: instanceId };
      } catch (error) {
        // Transport-level failure (offline, aborted). Still modal-scoped.
        return {
          error: "spawn_request_failed",
          kind: "spawn_failure",
          message:
            error instanceof Error
              ? error.message
              : GENERIC_SPAWN_FAILURE_MESSAGE,
          status: 0,
        };
      }
    },
    [],
  );

  const killInstance = useCallback(
    async (instanceId: string) => {
      try {
        const response = await fetch(`/user/instances/${instanceId}`, {
          credentials: "include",
          method: "DELETE",
        });
        if (!response.ok) {
          throw new Error(await readKillInstanceErrorMessage(response));
        }
        // Advance this instance's ordering marker so realtime/REST state emitted
        // before the kill committed is ordered behind it. The merge's terminal
        // guard is the real backstop (an in-flight snapshot can carry an equal
        // marker), but without this a late pre-kill delta would still be applied.
        instanceRealtimeMarkersRef.current.set(instanceId, Date.now());
        // The marker advance also orders the server's own killed event behind us,
        // so it can be dropped at the marker gate. Do its work here rather than
        // waiting on it: clear the row's live activation fields and the
        // generating marker, or the fleet counters keep counting a dead actor as
        // running until the generating TTL expires.
        setInstances((prev) => applyLocalKillToInstances(prev, instanceId));
        clearLiveActivationState(instanceId);
      } catch (error) {
        setErrorMessage(
          error instanceof Error ? error.message : "Failed to kill instance",
        );
      }
    },
    [clearLiveActivationState],
  );

  const sendMessage = useCallback(
    async (instanceId: string, channelId: string, message: string) => {
      const response = await fetch(
        `/user/instances/${instanceId}/channels/${channelId}/messages`,
        {
          body: JSON.stringify({ message }),
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          method: "POST",
        },
      );
      if (!response.ok) {
        throw new Error(`Failed to send message: ${response.status}`);
      }
      // Return the server-assigned message_id so callers can use it as the
      // Optimistic message ID. This prevents double-render when the WS echo
      // Arrives: both share the same ID and the merge dedup catches it.
      const result = sendMessageResponseSchema.parse(await response.json());
      return result.message_id;
    },
    [],
  );

  return {
    actorAttributions,
    error: errorMessage,
    generatingInstances,
    instances,
    killInstance,
    loading,
    refresh: fetchInstances,
    sendMessage,
    spawnInstance,
  };
}

function spawnRequestBody(input: SpawnInput) {
  return {
    displayName: input.displayName,
    ...(input.modelRef && { modelRef: input.modelRef }),
    ...(input.sharedChannelId && { sharedChannelId: input.sharedChannelId }),
    templateId: input.templateId,
  };
}
